<template>
    <!-- Pause duration -->
    <modal
            :show-back-btn="showBackBtn"
            :show-close-btn="showCloseBtn"
            :url-navigated-on-close="isOnPlaylistStep ? currentUrl : null"
            :slide="true"
            v-on:back="goToPrevStep"
            v-on:closed="onModalClosed"
            ref="modal">

        <div slot="header" slot-scope="modal">
            <p class="buy-now-header">{{ header }}</p>
            <p v-if="isOnQtyStep" class="buy-now-header-second">{{ order && order.coffeeName }}?</p>
            <p v-if="isOnShippingAndBillingStep && !isUserSignedIn && !onboarding" class="buy-now-header-second-sm">or <span @click="goToLoginStep"><strong>log in</strong></span></p>
        </div>

        <slot slot-scope="modal">
            <p v-if="error" class="error-msg margin-top-sm">{{ error }}</p>

            <buy-now-qty
                    v-if="isOnQtyStep"
                    :order="order"
                    :buy-now-discounts="buyNowDiscounts"
                    :custom-bag-discounts="customBagDiscounts"
                    :tax-rate="taxRate"
                    v-on:next="onQtySelected"
            />

            <buy-now-shipping-and-billing
                    v-show="isOnShippingAndBillingStep"
                    :stripe-publishable-key="stripePublishableKey"
                    :sign-up-error="signUpError"
                    :is-user-signed-in="isUserSignedIn"
                    :has-billing-info="!!creditCardLastDigits"
                    :user-data="userData"
                    :onboarding="onboarding"
                    :sign-up-only="onboarding ? signUp : false"
                    :subscribe="onboarding ? onboardingOrderPreferences.subscribe : false"
                    :is-gift="isGift"
                    :has-enough-credit="order && applicableCreditAmountCents >= order.totalPrice"
                    v-on:submit="onShippingAndBillingCompleted"
                    v-on:login="goToLoginStep"
            />

            <buy-new-account
                    v-if="isOnNewAccountStep"
            />

            <buy-now-confirmation
                    v-if="isOnConfirmationStep"
                    :has-shipping-and-billing-details="hasShippingAndBillingDetails"
                    :order="order"
                    :address="orderShipping"
                    :name="isGift ? giftData.friendName : username"
                    :shipping="orderShipping || shipping"
                    :is-gift="isGift"
                    :credit-card-last-digits="creditCardLastDigits"
                    :applicable-credit-amount-cents="applicableCreditAmountCents"
                    :tax-rate="taxRate"
                    v-on:confirmed="onOrderConfirmed"
            />

            <buy-now-sign-up-confirmation
                    v-if="isOnSignUpConfirmationStep"
                    :address="orderShipping"
                    :subscribe="onboardingOrderPreferences.subscribe"
                    :size-category="onboardingOrderPreferences.sizecategory"
                    :username="accountData.firstName + ' ' + accountData.lastName"
                    v-on:confirmed="onSignUpConfirmed"
            />

            <buy-now-playlist
                    v-if="isOnPlaylistStep"
                    :playlist-data="playlistData"
                    :current-url="currentUrl"
                    :first-time-to-active-playlist="!hasShippingAndBillingDetails"
                    v-on:activate="activatePlaylist"
            />

            <buy-now-confirmation-onboarding
                    v-if="isOnConfirmationOnboardingStep"
                    :order="order"
                    :sign-up-only="signUp"
                    :custom-sizes="customSizes"
                    :order-preferences="onboardingOrderPreferences"
                    :tax-rate="taxRate"
                    v-on:confirmed="onOrderConfirmed"
            />

            <buy-now-account-new-account-reminder
                    v-if="isOnNewAccountReminderStep"
            />

            <buy-now-login
                    :order="order"
                    :current-url="currentUrl"
                    v-if="isOnLoginStep"
            />

            <buy-now-gift-complete
                    v-if="isOnGiftCompleteStep"
                    :is-user-signed-in="isUserSignedIn"
                    v-on:close="$refs.modal.close()"
            />

            <div v-if="loadingMessage">
                {{ loadingMessage }}
            </div>
        </slot>
    </modal>
</template>

<script>
    import Modal from '../modal-home';
    import BuyNowQty from './buy-now-qty';
    import BuyNowShippingAndBilling from './buy-now-shipping-and-billing';
    import BuyNewAccount from './buy-now-new-account';
    import BuyNowConfirmation from './buy-now-confirmation';
    import BuyNowSignUpConfirmation from './buy-now-sign-up-confirmation';
    import BuyNowPlaylist from './buy-now-playlist';
    import BuyNowConfirmationOnboarding from './buy-now-confirmation-onboarding';
    import BuyNowAccountNewAccountReminder from './buy-now-new-account-reminder';
    import BuyNowLogin from './buy-now-login';
    import BuyNowGiftComplete from './buy-now-gift-complete';
    import BuyNowMixin from '../../../mixins/buy-now-mixin';
    import CoffeeMixin from '../../../mixins/coffee-mixin';
    import GaMixin from '../../../mixins/google-analytics-mixin'

    const STEPS = {
        QTY: 'step qty',
        SHIPPING_AND_BILLING: 'step shipping and billing',
        NEW_ACCOUNT: 'step new account',
        CONFIRMATION: 'step confirmation',
        SING_UP_CONFIRMATION: 'step sign up confirmation',
        PLAYLIST: 'step playlist',
        CONFIRMATION_ONBOARDING: 'step confirmation onboarding',
        NEW_ACCOUNT_REMINDER: 'step new account reminder',
        LOGIN: 'step login',
        LOADING: 'step loading',
        GIFT_COMPLETE: 'step gift complete'
    };

    export default {
        name: 'buy-now-form-shell',
        components: {
            Modal,
            BuyNowQty,
            BuyNowShippingAndBilling,
            BuyNewAccount,
            BuyNowConfirmation,
            BuyNowSignUpConfirmation,
            BuyNowPlaylist,
            BuyNowConfirmationOnboarding,
            BuyNowAccountNewAccountReminder,
            BuyNowLogin,
            BuyNowGiftComplete
        },
        mixins: [
            BuyNowMixin,
            CoffeeMixin,
            GaMixin,
        ],
        props: {
            order: Object,
            buyNowDiscounts: Object,
            customBagDiscounts: Array,
            isUserSignedIn: Boolean,
            stripePublishableKey: String,
            username: String,
            email: String,
            shipping: Object,
            creditCardLastDigits: String,
            applicableCreditAmountCents: Number,
            currentUrl: String,
            onboarding: Boolean,
            onboardingOrderPreferences: Object,
            signUp: Boolean,
            taxRate: Number,
            customSizes: Array
        },
        data: function () {
            return {
                currentStep: STEPS.QTY,
                showBackBtn: false,
                showCloseBtn: true,
                loadingMessage: null,
                accountData: undefined,
                orderShipping: undefined,
                stripeTokenId: undefined,
                playlistData: undefined,
                error: undefined,
                signUpError: undefined,
                isGift: false,
                giftData: undefined,
                subscribeNewsletter: undefined
            }
        },
        methods: {
            // HACK: This Mutates a prop directory...
            setOrder(order) {
                this.order = order;
            },
            open(skipQty = false, signUpOnly = false) {
                if (!this.$refs.modal) {
                    return;
                }

                if (signUpOnly) {
                    // HACK Force mutate signUpOnly properly if it hasn't been updated...
                    if (!this.signUpOnly) {
                        this.signUpOnly = true;
                    }
                    this.currentStep = STEPS.SHIPPING_AND_BILLING;
                    this.$refs.modal.show();
                    return;
                }

                this.isGift = this.order.isGift;

                // if (this.order.isSampler || this.onboarding) {
                    if(this.onboarding){
                    this.order.qty = 1;
                    this.setPrice(this.order.qty);
                    skipQty = true;
                }

                if (skipQty) {
                    this.currentStep = STEPS.SHIPPING_AND_BILLING;
                }

                if (this.hasShippingAndBillingDetails && skipQty && !this.isGift) {
                    this.currentStep = STEPS.CONFIRMATION;
                }

                this.$refs.modal.show();
            },
            onQtySelected(qty) {
                this.order.qty = qty;
                this.setPrice(qty);
                this.goToNextStep((this.hasShippingAndBillingDetails && !this.isGift) ? STEPS.CONFIRMATION : STEPS.SHIPPING_AND_BILLING)
            },
            setPrice(qty) {
                this.order.discount = this.getDiscountCents(this.order.size, qty, this.buyNowDiscounts, this.customBagDiscounts);
                let totalPrice = this.calculateTotalPriceCents(qty, this.order.unitPrice, this.order.discount);
                this.order.tax = Math.ceil(totalPrice * this.taxRate);
                this.order.totalPrice = this.calcTaxedPriceCents(totalPrice, this.taxRate);
            },
            goToNextStep(nextStep) {
                // Wait until sliding animation ends
                setTimeout(() => this.currentStep = nextStep, 850);
                this.$refs.modal.slideInFromRight();
            },
            goToPrevStep() {
                this.$refs.modal.slideInFromLeft();
                let prevStep = undefined;

                switch (this.currentStep) {
                    case STEPS.SHIPPING_AND_BILLING:
                        prevStep = STEPS.QTY;
                        break;
                    case STEPS.LOGIN:
                        prevStep = STEPS.SHIPPING_AND_BILLING;
                        break;
                    case STEPS.CONFIRMATION:
                        prevStep = (this.hasShippingAndBillingDetails && !this.isGift) ? STEPS.QTY : STEPS.SHIPPING_AND_BILLING;
                        break;
                }
                // Wait until sliding animation ends
                setTimeout(() => this.currentStep = prevStep, 850);
            },
            processOrder() {
                this.$api.post('v1/buy_now/process_order', this.getOrderParams())
                    .then(res => this.onOrderProcessed())
                    .catch(err => {
                        this.error = 'Sorry, we were unable to process your order. Please update your billing, or contact support@crema.co';
                        this.showCloseBtn = true;
                    })
            },
            onOrderProcessed() {
                this.sendOrderEvent('Buy Now - User Order', this.order.totalPrice);
                fbq('track', 'Purchase', {currency: "USD", value: this.order.totalPrice});
                this.$api.get('v1/playlists/status')
                    .then(res => {
                        if (res || res.data) {
                            this.playlistData = res.data;
                            if (this.onboarding) {
                                this.$emit('order-completed');
                            } else {
                                this.goToNextStep(STEPS.PLAYLIST);
                            }
                        }
                    })
                    .catch()
            },
            getOrderParams() {
                return {
                    coffeeId: this.order.coffeeId,
                    qty: this.order.qty,
                    size: this.order.size,
                    grind: this.order.grind,
                    onboarding: this.onboarding,
                    subscribe: this.onboarding ? this.onboardingOrderPreferences.subscribe : false,
                    sizeCategory: this.onboarding ? this.onboardingOrderPreferences.sizecategory : ""
                }
            },
            processGiftOrder() {
                this.$api.post('v1/buy_now/process_gift_order', this.getGiftOrderParams())
                    .then(res => {
                        fbq('track', 'Purchase', {currency: "USD", value: this.order.totalPrice});
                        this.sendOrderEvent('Send to Friend', this.order.totalPrice);
                        this.goToNextStep(STEPS.GIFT_COMPLETE);
                    })
                    .catch(err => {
                        this.error = 'Sorry, we were unable to process your order. Please update your billing, or contact support@crema.co';
                        this.showCloseBtn = true;
                    })
            },
            getGiftOrderParams() {
                return {
                    coffeeId: this.order.coffeeId,
                    qty: this.order.qty,
                    size: this.order.size,
                    grind: this.order.grind,
                    friendName: this.giftData.friendName,
                    friendEmail: this.giftData.friendEmail,
                    friendAddress: this.orderShipping,
                    message: this.giftData.message,
                    gifterName: this.giftData.gifterName,
                    gifterEmail: this.giftData.gifterEmail,
                    stripeTokenId: this.stripeTokenId,
                    reveal_type: (this.giftData.sendEmail && this.giftData.friendEmail && this.giftData.friendEmail.length) ? 'email' : ''
                }
            },
            onShippingAndBillingCompleted(accountData, address, stripeTokenId, giftData) {
                this.showBackBtn = false;
                this.accountData = accountData;
                this.orderShipping = address;
                this.stripeTokenId = stripeTokenId;

                this.fetchTaxRate(address.state).then(
                    res => {
                        this.taxRate = res.data;
                        if (this.signUpOnly) {
                            this.goToNextStep(STEPS.SING_UP_CONFIRMATION);
                            return;
                        }
                        else if (this.onboarding) {
                            fbq('track', 'Purchase', {currency: "USD", value: this.order.totalPrice});
                            this.goToNextStep(STEPS.CONFIRMATION_ONBOARDING);
                            return;
                        }

                        this.giftData = giftData;
                        this.goToNextStep(STEPS.CONFIRMATION);
                    }
                ).catch();
            },
            fetchTaxRate(state) {
                return this.$api.get('v1/buy_now/tax_rate', {
                    params: {
                        state: state,
                    }
                });
            },
            onOrderConfirmed() {
                this.showBackBtn = false;
                this.showCloseBtn = false;

                if (this.onboarding) {
                    fbq('track', 'Onboarding: Order Confirmed');
                }
                if (this.isGift) {
                    this.processGiftOrder();
                } else if (this.hasShippingAndBillingDetails) {
                    this.processOrder();
                } else if (this.isUserSignedIn) {
                    this.updateAccount();
                } else if (this.accountData.password) {
                    this.createAccountBeforeOrder();
                } else {
                    this.processGuestOrder();
                }
            },
            onSignUpConfirmed() {
                let params = this.getAccountParams();
                params['onboardingCredit'] = true;
                params['subscribe'] = this.onboardingOrderPreferences.subscribe;

                this.$api.post('v1/accounts', params)
                    .then(res => {
                        this.signUpError = null;
                        this.$emit('account-created', null, true);
                    })
                    .catch(err => {
                        if (err && err.response && err.response.status === 422) {
                            this.signUpError = 'This email address is already taken.';
                        } else {
                            this.signUpError = 'Sorry, we could not create your order. Please try it again.';
                        }
                        this.goToPrevStep();
                    })
            },
            updateAccount() {
                this.$api.patch('v1/accounts', this.getAccountParams())
                    .then(() => {
                        this.signUpError = null;
                        this.skip_next_brewlist_batch();
                        this.processOrder();
                    })
                    .catch(() => {
                        this.signUpError = 'Sorry, we could not create your order. Please try it again.';
                        this.goToPrevStep();
                    })
            },
            createAccountBeforeOrder() {
                let params = this.getAccountParams();

                this.$api.post('v1/accounts', params)
                    .then(res => {
                        this.signUpError = null;

                        if (this.onboarding) {
                            this.$emit('account-created', this.order.coffeeId, false);
                        } else {
                            this.sendSignupEvent('Buy Now Sign-up');
                        }
                        this.skip_next_brewlist_batch();
                        this.processOrder();
                    })
                    .catch(err => {
                        if (err && err.response && err.response.status === 422) {
                            this.signUpError = 'This email address is already taken.';
                        } else {
                            this.signUpError = 'Sorry, we could not create your order. Please try it again.';
                        }
                        this.goToPrevStep();
                    })
            },
            getAccountParams() {
                return {
                    user: {
                        ...this.convertAccountData(this.accountData),
                        ...this.convertAddressParams(this.orderShipping),
                        optin_communication: this.subscribeNewsLetter
                    },
                    stripeTokenId: this.stripeTokenId
                }
            },
            convertAccountData(accountData) {
                return {
                    name: `${accountData.firstName} ${accountData.lastName}`,
                    email: accountData.email,
                    password: accountData.password
                }
            },
            convertAddressParams(address) {
                return {
                    shippingStreet1: address.street1,
                    shippingStreet2: address.street2,
                    shippingCity: address.city,
                    shippingState: address.state,
                    shippingPostalCode: address.postalCode,
                    shippingCountry: 'US',
                    skipAddressVerification: address.validationSkipped
                }
            },
            processGuestOrder() {
                this.$api.post('v1/buy_now/process_guest_order', this.getGuestOrderParams())
                    .then(() => {
                        fbq('track', 'Purchase', {currency: "USD", value: this.order.totalPrice});
                        this.sendOrderEvent('Buy Now - Guest Order', this.order.totalPrice);
                        this.goToNextStep(STEPS.NEW_ACCOUNT_REMINDER);
                    })
                    .catch(err => {
                        this.error = 'Sorry, we were unable to process your order. Please update your billing, or contact support@crema.co';
                        this.showCloseBtn = true;
                    })
            },
            getGuestOrderParams() {
                return {
                    ...this.convertAccountData(this.accountData),
                    ...this.convertAddressParams(this.orderShipping),
                    ...this.getOrderParams(),
                    stripeTokenId: this.stripeTokenId
                }
            },
            // createAccountAfterOrder(email, password) {
            //     this.accountData.email = email;
            //     this.accountData.password = password;
            //     this.$api.post('v1/accounts', this.getAccountParams(this.addressSelected))
            //         .then(() => {
            //             this.skip_next_brewlist_batch();
            //             this.reloadAndOpenPlaylist();
            //         })
            //         .catch(err => {
            //             if (err && err.response && err.response.status === 422) {
            //                 this.signUpError = 'This email address is already taken.';
            //             } else {
            //                 this.signUpError = 'Sorry, we could not create your account. Please try it again.';
            //             }
            //         })
            // },
            activatePlaylist() {
                this.$api.patch('v1/playlists/pause', { pause: false })
                    .then(() => this.$api.patch('v1/playlists/skip_next_batch', {})
                        // TODO: Error handling
                        .then(() => window.location.replace('/coffees?start_tour=true')
                    ).catch(err => console.error(err)))
            },
            onModalClosed() {
                if (this.currentStep === STEPS.PLAYLIST_ONBOARDING) {
                    window.location.replace('/coffees');
                } else {
                    this.$emit('closed');
                }
            },
            reloadAndOpenPlaylist(target_url = undefined) {
                this.loading = true;
                // if (!target_url) {
                //     target_url = this.currentUrl + this.currentUrl.includes('?') ? '&open_playlist=true' : '?open_playlist=true';
                // }
                window.location.replace(target_url);
            },
            skip_next_brewlist_batch() {
                this.$api.patch('v1/playlists/skip_next_batch', {})
                    .then(() => {})
                    .catch((err) => console.error(err))
            },
            goToLoginStep() {
                this.goToNextStep(STEPS.LOGIN);
            }
        },
        computed: {
            isOnQtyStep: function() {
                return this.currentStep === STEPS.QTY;
            },
            isOnShippingAndBillingStep: function() {
                return this.currentStep === STEPS.SHIPPING_AND_BILLING;
            },
            isOnNewAccountStep: function() {
                return this.currentStep === STEPS.NEW_ACCOUNT;
            },
            isOnConfirmationStep: function() {
                return this.currentStep === STEPS.CONFIRMATION;
            },
            isOnSignUpConfirmationStep: function() {
                return this.currentStep === STEPS.SING_UP_CONFIRMATION;
            },
            isOnPlaylistStep: function() {
                return this.currentStep === STEPS.PLAYLIST;
            },
            isOnConfirmationOnboardingStep: function() {
                return this.currentStep === STEPS.CONFIRMATION_ONBOARDING;
            },
            isOnNewAccountReminderStep: function() {
                return this.currentStep === STEPS.NEW_ACCOUNT_REMINDER;
            },
            isOnLoginStep: function() {
                return this.currentStep === STEPS.LOGIN;
            },
            isOnGiftCompleteStep: function() {
                return this.currentStep === STEPS.GIFT_COMPLETE;
            },
            header: function() {
                switch (this.currentStep) {
                    case STEPS.QTY:
                        return this.order.isSampler ? `How many of the` : `How many bags of`
                    case STEPS.SHIPPING_AND_BILLING:
                        return this.isGift ? 'Who’s the lucky duck?' : 'Shipping & Billing';
                    case STEPS.NEW_ACCOUNT:
                        return '';
                    case STEPS.CONFIRMATION:
                        return 'Confirmation';
                    case STEPS.SING_UP_CONFIRMATION:
                        return 'Confirmation';
                    case STEPS.PLAYLIST:
                        return 'Brew Queue';
                    case STEPS.CONFIRMATION_ONBOARDING:
                        return 'Confirmation';
                    case STEPS.NEW_ACCOUNT_REMINDER:
                        return 'Create an account';
                    case STEPS.LOGIN:
                        return 'Login';
                    case STEPS.GIFT_COMPLETE:
                        return 'It\'s off to the roaster';
                    default:
                        return '';
                }
            },
            userData: function() {
                return {
                    username: this.username,
                    email: this.email,
                    shipping: this.shipping
                }
            },
            hasShippingAndBillingDetails: function() {
                return (!!this.shipping && !!this.creditCardLastDigits);
            }
        },
        watch: {
            currentStep: function () {
                switch (this.currentStep) {
                    case STEPS.QTY:
                        this.showBackBtn = false;
                        break;
                    case STEPS.SHIPPING_AND_BILLING:
                        this.showBackBtn = true;
                        if (this.onboarding || this.signUpOnly || this.order.isSampler) {
                            this.showBackBtn = false;
                        }
                        break;
                    case STEPS.LOGIN:
                        this.showBackBtn = true;
                        break;
                    case STEPS.NEW_ACCOUNT_REMINDER:
                        this.showCloseBtn = true;
                        break;
                    case STEPS.CONFIRMATION:
                        this.showBackBtn = true;
                        if (this.order.isSampler && this.hasShippingAndBillingDetails) {
                            this.showBackBtn = false;
                        }
                        break;
                    case STEPS.PLAYLIST:
                        this.showBackBtn = false;
                        this.showCloseBtn = true;
                        break;
                    case STEPS.GIFT_COMPLETE:
                        this.showBackBtn = false;
                        this.showCloseBtn = true;
                        break;
                }
            }
        }
    }
</script>

<style lang="scss">
    @import "../../../styles/variables_home";
    @import "../../../styles/common_home";

    .buy-now-header {
        font-size: 20px;
        text-align: center;
        margin: 0;
    }
    .buy-now-header-second {
        @extend .buy-now-header;
        font-size: 18px;
        margin-top: 8px;
    }
    .buy-now-header-second-sm {
        @extend .buy-now-header-second;
        font-size: 16px;
        margin: 6px 0 0 0;
        text-align: center;
        span {
            cursor: pointer;
        }
    }
    .error-msg {
        color: $red;
        padding: 0 1rem 1rem 1rem;
        text-align: center;
    }
    .modal-body h3 {
        font-weight: bold;
        color: $color-primary;
        letter-spacing: -0.5px;
    }
</style>